import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Flex, Tabs } from "antd";
import styled from "styled-components";
import Button from "../Button";
import { CaretLeftOutlined, CaretRightOutlined } from "@ant-design/icons";
import PropTypes from "prop-types"; // Added for prop type validation
import { TabContainer } from "./styles";

// Default tab items
const sampleItems = [
  {
    label: "Tab 1",
    key: "0",
    children: "Tab 1",
    icon: "",
    destroyInactiveTabPane: false,
    closable: true,
    closeIcon: "",
  },
  {
    label: "Tab 2",
    key: "1",
    children: "Tab 2",
    disabled: false,
  },
  {
    label: "Tab 3",
    key: "2",
    children: "Tab 3",
  },
];

// Default token for tab styles
const defaultTabsToken = {
  cardBg: "",
  cardGutter: "",
  cardHeight: "",
  cardPadding: "15px",
  inkBarColor: "#eef0f2",
  itemActiveColor: "#0958d9",
  itemColor: "rgb(21, 33, 41)",
  itemHoverColor: "#4096ff",
  titleFontSize: 14,
  horizontalItemPadding: "12px 0",
  verticalItemMargin: "16px 0 0 0",
  zIndexPopup: 1050,
};

const TabsV2 = forwardRef(
  ({
    items = sampleItems,
    activeKey,
    defaultActiveKey,
    renderTabBar,
    onChange,
    onEdit,
    onTabClick,
    popupClassName,
    onTabScroll,
    type = "card",
    centered = false,
    size = "middle",
    tabPosition = "top", // top | right | bottom | left
    tabBarGutter = "0",
    tabBarStyle,
    showExtra = true,
    more,
    tabsToken = defaultTabsToken,
    tabBarLeftExtra,
    onNextClickTrigger,
    nextBtnRef,
    className,
    navButtonsColor
  }) => {
    const [tabActiveKey, setTabActiveKey] = useState(defaultActiveKey);

    // Deriving activeKey to use
    const activeKeyToUse = activeKey || tabActiveKey;

    // Synchronize active tab with props when activeKey changes
    useEffect(() => {
      if (activeKey) {
        setTabActiveKey(activeKey);
      }
    }, [activeKey]);

    // Sync active key with items when the items change
    useEffect(() => {
      if (items.length > 0 && !activeKey && !tabActiveKey) {
        setTabActiveKey(items[0].key);
      }
    }, [items, activeKey, tabActiveKey]);

    // Handle tab change
    const onTabChange = (key) => {
      const selectedItem = items.find((item) => item.key === key);

      if (!activeKey) {
        setTabActiveKey(key);
      }
      // onChange?.(selectedItem || key);
      onChange?.(key);
    };

    const onBackClick = () => {
      const currentIndex = items.findIndex(
        (item) => item.key === activeKeyToUse
      );

      // Check if currentIndex is valid
      if (currentIndex > 0) {
        if( items[currentIndex - 1].disabled) {
          return
        }
        const newActiveKey =  items[currentIndex - 1].key;
        setTabActiveKey(newActiveKey); // Update the active key state
        onChange?.(newActiveKey); // Notify parent component if needed
      }
    };

    const onNextClick = () => {
      
      const currentIndex = items.findIndex(
        (item) => item.key === activeKeyToUse
      );
      if( items[currentIndex + 1].disabled) {
        return
      }
      // Check if currentIndex is valid
      if (currentIndex < items.length - 1) {
        const newActiveKey = items[currentIndex + 1].key;
        setTabActiveKey(newActiveKey); // Update the active key state
        onChange?.(newActiveKey); // Notify parent component if needed
      }
    };

    const onHandleNextClick = () => {
      if(onNextClickTrigger){
        onNextClickTrigger()
      }
      else{ onNextClick() }
    }

    // Tab bar actions with dynamic left and right extra components
    const tabBarRightExtra = (
      <>
        <Button
          color={tabPosition == "left" ? navButtonsColor ?? "primary" : navButtonsColor ?? "warning"}
          type={tabPosition == "left" ? navButtonsColor ?? "primary" : navButtonsColor ?? "warning"}
          onClick={onBackClick}
          icon={<CaretLeftOutlined />}
          disabled={
            items.findIndex((item) => item.key === activeKeyToUse) === 0
          }
        />
        <Button
          color={tabPosition == "left" ? navButtonsColor ?? "primary" : navButtonsColor ?? "warning"}
          type={tabPosition == "left" ? navButtonsColor ?? "primary" : navButtonsColor ?? "warning"}
          onClick={onHandleNextClick}
          iconPosition="end"
          disabled={
            items.findIndex((item) => item.key === activeKeyToUse) ===
            items.length - 1
          }
          icon={<CaretRightOutlined />}
          style={{ marginRight: "10px" }}
        />
      </>
    );

    const tabBarActions = {
      right: (
        <Flex gap="10px" style={{ marginLeft: "10px" }}>
          {showExtra && tabBarLeftExtra ? tabBarLeftExtra : null}
          {tabBarRightExtra}
        </Flex>
      ),
    };

    useImperativeHandle(nextBtnRef, () => ({
      onNextClick: () => onNextClick(),
    }));

    return (
      <TabContainer className="styled-tab-container" theme={{ tabsToken }}>
        <Tabs
          type={type}
          activeKey={activeKeyToUse}
          defaultActiveKey={defaultActiveKey}
          tabBarExtraContent={showExtra ? tabBarActions : null}
          style={{ width: "100%" }}
          items={items}
          onChange={onTabChange}
          onTabClick={onTabClick}
          onTabScroll={onTabScroll}
          popupClassName={popupClassName}
          onEdit={onEdit}
          centered={centered}
          size={size}
          tabBarGutter={tabBarGutter}
          tabBarStyle={tabBarStyle}
          tabPosition={tabPosition}
          renderTabBar={renderTabBar}
          more={more}
          className={className}
        />
      </TabContainer>
    );
  }
);

export default TabsV2;
